import { Button } from '@/components/ui/button';
import { EasyIcon, ExhaustiveIcon, InnovationIcon, PremiumIcon } from '@/components/ui/icons';
import { AdvantageCard } from '@/components/layout/home/advantage-card';
import { MOTION } from '@/utils';
import { ADVANTAGES, type AdvantageData, type AdvantageIcon } from '@/utils/data/advantages';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { useRef, type ComponentType } from 'react';
import { Tag } from '@/components/ui/tag';

const ADVANTAGE_ICONS: Record<AdvantageIcon, ComponentType> = {
  exhaustive: ExhaustiveIcon,
  easy: EasyIcon,
  premium: PremiumIcon,
  innovation: InnovationIcon,
};

const withPlusLabel = (item: string) =>
  item === 'Et + encore...' ? (
    <>
      Et <span className="text-orange font-bold">+</span> encore...
    </>
  ) : (
    item
  );

const formatItems = (advantage: AdvantageData) =>
  advantage.FORMAT_PLUS_LABEL ? advantage.ITEMS.map(withPlusLabel) : [...advantage.ITEMS];

export default function Advantage() {
  const refs = {
    section: useRef(null),
    tag: useRef(null),
    title: useRef(null),
    intro: useRef(null),
    button: useRef(null),
  };

  useGSAP(
    () => {
      if (!refs.title.current || !refs.intro.current) return;

      const splitTitle = new SplitText(refs.title.current, { type: 'lines', mask: 'lines' });
      const splitIntro = new SplitText(refs.intro.current, { type: 'lines', mask: 'lines' });

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(refs.tag.current, {
        autoAlpha: 1,
        y: 0,
        duration: MOTION.durationSm,
        ease: MOTION.ease,
      })
        .from(
          splitTitle.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.15',
        )
        .from(
          splitIntro.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.2',
        )
        .from(
          refs.button.current,
          {
            autoAlpha: 0,
            y: 8,
            duration: MOTION.durationSm,
            ease: MOTION.ease,
          },
          '<+0.15',
        );

      return () => {
        splitTitle.revert();
        splitIntro.revert();
      };
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-blue-section mx-auto flex flex-col items-start gap-12 px-6 py-16 md:px-8"
      id="solutions"
    >
      <div className="flex w-full flex-col items-start gap-3">
        <Tag ref={refs.tag}>Pourquoi zLawyer ?</Tag>

        <h2 ref={refs.title}>Les avantages de zLawyer</h2>

        <p ref={refs.intro} aria-hidden="true" className="max-w-3xl leading-relaxed">
          Le logiciel de gestion pensé par et pour les cabinets d&apos;avocats. La puissance de
          zLawyer repose sur un équilibre unique entre simplicité d&apos;utilisation, richesse
          fonctionnelle et qualité de service.
        </p>

        <p className="sr-only">
          Le logiciel de gestion pensé par et pour les cabinets d&apos;avocats. La puissance de
          zLawyer repose sur un équilibre unique entre simplicité d&apos;utilisation, richesse
          fonctionnelle et qualité de service.
        </p>
      </div>

      <div className="grid w-full grid-cols-1 gap-5 md:grid-cols-2">
        {ADVANTAGES.map((advantage, index) => {
          const Icon = ADVANTAGE_ICONS[advantage.ICON];

          return (
            <AdvantageCard
              key={advantage.TITLE}
              description={advantage.DESCRIPTION}
              footer={advantage.FOOTER}
              icon={<Icon />}
              items={formatItems(advantage)}
              tagline={advantage.TAGLINE}
              title={advantage.TITLE}
            />
          );
        })}
      </div>

      <Button ref={refs.button} className="w-fit text-center" href="/contact/">
        Demandez une démo
      </Button>
    </section>
  );
}
